import { apiClient } from './client';
import type { SystemRole } from '@/types';

// ── Response shapes — kept in lockstep with server/src/routes/auth.routes.ts ──

export interface MembershipSummary {
  id: string;
  role: SystemRole;
  organizationId: string;
  organization: { id: string; name: string; slug: string };
}

export interface AuthMeResponse {
  user: { id: string; email: string; firstName: string; lastName: string };
  membership: { id: string; role: SystemRole; organizationId: string };
  organization: { id: string; name: string; slug: string };
  /** Every ACTIVE membership across orgs, oldest-first — feeds the org
   *  switcher. Optional so a cached older /me response can't crash the UI. */
  memberships?: MembershipSummary[];
  /** Explicit capability grants (ADR-017). Superadmins hold every
   *  capability implicitly — combine with role via `hasCapability`
   *  in src/lib/capabilities.ts, do not read this raw. Optional so
   *  a cached older /me response can't crash the UI. */
  capabilities?: string[];
  impersonation: { realRole: SystemRole } | null;
}

export interface MagicLinkStartResponse {
  ok: true;
  /** Only set when the server is in MOCK_AUTH=true mode (dev/CI). */
  devToken?: string;
}

export type MagicLinkVerifyResponse =
  | (AuthMeResponse & {
      ok: true;
      needsOnboarding: false;
      /** Verify additionally reports whether the person has ever
       *  explicitly saved their profile — gates the one-time
       *  "confirm your name" interstitial. */
      user: AuthMeResponse['user'] & { profileCompletedAt: string | null };
    })
  | {
      ok: true;
      user: AuthMeResponse['user'];
      memberships: never[];
      needsOnboarding: true;
    };

// ── Endpoints ──

export async function requestMagicLink(email: string): Promise<MagicLinkStartResponse> {
  const res = await apiClient.post<MagicLinkStartResponse>('/api/auth/magic-link/start', { email });
  return res.data;
}

export async function verifyMagicLink(token: string): Promise<MagicLinkVerifyResponse> {
  const res = await apiClient.post<MagicLinkVerifyResponse>('/api/auth/magic-link/verify', { token });
  return res.data;
}

export async function getMe(): Promise<AuthMeResponse> {
  const res = await apiClient.get<AuthMeResponse>('/api/auth/me');
  return res.data;
}

export async function logout(): Promise<void> {
  await apiClient.post('/api/auth/logout');
}

/**
 * Switch the active organization. The server re-mints the session cookie
 * scoped to the target org and responds with the /me shape for it.
 */
export async function switchOrg(organizationId: string): Promise<AuthMeResponse> {
  const res = await apiClient.post<AuthMeResponse>('/api/auth/switch-org', { organizationId });
  return res.data;
}

export interface UpdateProfileInput {
  firstName: string;
  lastName: string;
  /** Pass `null` to clear the avatar; omit to leave it untouched. */
  avatarUrl?: string | null;
}

export interface ProfileUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
}

export async function updateProfile(input: UpdateProfileInput): Promise<ProfileUser> {
  const res = await apiClient.patch<{ data: { user: ProfileUser } }>('/api/auth/profile', input);
  return res.data.data.user;
}
